import { useState } from 'react';
import { Button, Input, FormModal, List, LoadingSpinner } from '@airclou/ui';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react'
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next';
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateInTz } from '@airclou/utils'

interface DomainConfig {
  id: string;
  domain: string;
  verified: boolean;
  created_at: string;
}

export function DomainsTab(props: {
  domains?: DomainConfig[];
  /** The config fetch is still running, so "no domains" is not yet an answer. */
  isLoading?: boolean;
  newDomain: string;
  setNewDomain: (v: string) => void;
  onAddDomain: () => void;
  onRemoveDomain: (domain: { domain: string; verified: boolean }) => void;
}) {
  const timezone = useTimezone()
  const { domains, isLoading = false, newDomain, setNewDomain, onAddDomain, onRemoveDomain } = props;
  const { t } = useTranslation('dashboard');
  const [showAddModal, setShowAddModal] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-gray-600 dark:text-gray-300">
          {t('sso.domainsDescription')}
        </p>
        <Button onClick={() => setShowAddModal(true)}>{t('sso.addDomain')}</Button>
      </div>
 
      {/* Add Domain Modal */}
      <FormModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={t('sso.addDomain')}
        size="sm"
        submitText={t('sso.addDomain')}
        canSubmit={newDomain.trim().length > 0}
        onSubmit={() => {
          onAddDomain();
          setShowAddModal(false);
        }}
      >
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('sso.domain')}</label>
          <Input
            type="text"
            value={newDomain}
            onChange={(value) => setNewDomain(value)}
            placeholder="example.com"
          />
        </div>
      </FormModal>

      {/* Domain list (compact, divided with actions menu) */}
      {domains && domains.length > 0 ? (
        <List>
          {domains.map((d) => {
            const isVerified = d.verified;
            const createdISO = new Date(d.created_at).toISOString();
            const createdLabel = formatDateInTz(d.created_at, timezone);
            return (
              <List.Item
                key={d.id}
                title={d.domain}
                status={isVerified ? (
                  <p className="mt-0.5 rounded-md bg-green-50 px-1.5 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20 dark:bg-green-400/10 dark:text-green-400 dark:ring-green-500/20">
                    {t('sso.verified')}
                  </p>
                ) : (
                  <p className="mt-0.5 rounded-md bg-yellow-50 px-1.5 py-0.5 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20 dark:bg-yellow-400/10 dark:text-yellow-500 dark:ring-yellow-400/20">
                    {t('common.pending')}
                  </p>
                )}
                subtitle={(
                  <div>
                    <p className="whitespace-nowrap">
                      {t('sso.addedOn')} <time dateTime={createdISO}>{createdLabel}</time>
                    </p>
                    {/* No Verify button: verifying checks no ownership yet, so the
                        server lets only Airclou support do it (OD-15 in
                        docs/plans/one-organization-per-domain-plan.md). */}
                    {!isVerified && <p>{t('sso.domainVerifySupportOnly')}</p>}
                  </div>
                )}
                right={(
                  <div className="flex shrink-0 items-center gap-x-4">
                    <Menu as="div" className="relative flex-none">
                      <MenuButton className="relative block text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
                        <span className="sr-only">{t('sso.openOptions')}</span>
                        <EllipsisVerticalIcon aria-hidden="true" className="h-5 w-5" />
                      </MenuButton>
                      {/* Anchored so the menu floats above the page: the List
                          clips overflow, which cut off an absolutely-positioned menu. */}
                      <MenuItems
                        anchor="bottom end"
                        className="z-10 w-36 origin-top-right rounded-md [--anchor-gap:8px] bg-white py-2 shadow-lg outline outline-1 outline-gray-900/5 transition data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in dark:bg-gray-800 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10"
                      >
                        <MenuItem>
                          <button
                            type="button"
                            onClick={() => onRemoveDomain({ domain: d.domain, verified: d.verified })}
                            className="block w-full px-3 py-1 text-left text-sm data-[focus]:bg-gray-50 dark:data-[focus]:bg-white/5 text-red-600 dark:text-red-400"
                          >
                            {t('common.remove')}
                          </button>
                        </MenuItem>
                      </MenuItems>
                    </Menu>
                  </div>
                )}
              />
            );
          })}
        </List>
      ) : isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : (
        <p className="text-center text-gray-400 py-8">{t('sso.noDomainsConfigured')}</p>
      )}
    </div>
  );
}
